import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { labelBase, borderBase, inputBase, counterBtn, to12h, cruiseLabel } from '../components/shared/helpers';
import { supabase } from '../lib/supabase';
import { fetchAllCruiseTimes, fetchAllCruiseNames } from '../lib/cruiseDetails';

// Stored cruise_preference slugs are fixed (the enquiry pipeline and the staff
// app read them); the sailing times beside each name come from cruise_settings
// so they can never drift from the real schedule. Until they load — or if the
// read fails — the option shows the name alone rather than a guessed time.
const ENQUIRY_CRUISES = [
  { value: 'sunset',  cruiseName: 'Sunset Cruise' },
  { value: 'sundown', cruiseName: 'Sundown Cruise' },
  { value: 'dinner',  cruiseName: 'Dinner Cruise' },
];

export default function Enquiry() {
  const { isVerified, fading, go } = useBooking();

  const [enquiryForm, setEnquiryForm] = useState({ name: '', phone: '', date: '', cruise: '', deck: '', adults: 1, children: 0, infants: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [cruiseTimes, setCruiseTimes] = useState({}); // cruise_name → "5:00 PM – 6:00 PM"
  const [cruiseNames, setCruiseNames] = useState({}); // cruise_name → names row for cruiseLabel()

  // Guest-facing short names, keyed by the stored cruise_name (loaded once).
  // Only the option TEXT uses them; the submitted slug and the times lookup
  // stay keyed on the stored name.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const byName = await fetchAllCruiseNames();
      if (!cancelled) setCruiseNames(byName);
    })();
    return () => { cancelled = true; };
  }, []);

  // Sailing times via the shared cruiseDetails lookup, for the date the
  // guest has picked (none until they pick one).
  const enquiryDate = enquiryForm.date || null;
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const byName = await fetchAllCruiseTimes({ date: enquiryDate });
      if (cancelled) return;
      const times = {};
      for (const [name, c] of Object.entries(byName)) {
        const from = to12h(c.departure_time);
        const to = to12h(c.end_time);
        if (from && to) times[name] = `${from} – ${to}`;
      }
      setCruiseTimes(times);
    })();
    return () => { cancelled = true; };
  }, [enquiryDate]);

  const enquiryValid = Boolean(
    enquiryForm.name.trim() && enquiryForm.phone.trim() && enquiryForm.date && enquiryForm.cruise && enquiryForm.deck
  );

  const handleSubmit = async () => {
    if (!enquiryValid || submitting) return;
    setSubmitting(true);
    setSubmitError('');
    const { error } = await supabase.from('online_enquiries').insert({
      full_name: enquiryForm.name.trim(),
      phone: enquiryForm.phone.trim(),
      preferred_date: enquiryForm.date,
      cruise_preference: enquiryForm.cruise,
      deck_preference: enquiryForm.deck,
      adults: enquiryForm.adults,
      children: enquiryForm.children,
      infants: enquiryForm.infants,
      status: 'New',
    });
    setSubmitting(false);
    if (error) {
      setSubmitError('Something went wrong. Please try again.');
      return;
    }
    setSubmitted(true);
  };

  // Guard — must be verified
  if (!isVerified) return <Navigate to="/" replace />;

  if (submitted) {
    return (
      <div className={`w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>
        <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">
          <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
            Enquiry received
          </h1>
          <p className="font-dm-sans text-sm text-white/55 font-light mb-6 leading-relaxed">
            Thank you — we'll contact you closer to your preferred date based on availability.
          </p>
          <button
            type="button"
            onClick={() => go('/select-date')}
            className="w-full py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,opacity] duration-[200ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] hover:bg-[#B8973E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35] cursor-pointer"
          >
            Back to Dates
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>
        <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">

          {/* Back arrow */}
          <button
            type="button"
            onClick={() => go('/select-date')}
            className="flex items-center justify-center w-8 h-8 rounded-full border border-[rgba(212,168,67,0.3)] hover:border-[rgba(212,168,67,0.65)] hover:bg-[rgba(212,168,67,0.06)] transition-[border-color,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] mb-4"
            aria-label="Go back"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M9 2L4 7L9 12" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
            Plan Your Visit
          </h1>
          <p className="font-dm-sans text-sm text-white/55 font-light mb-5 leading-relaxed">
            We'll get in touch to help you plan your perfect cruise
          </p>

          <div className="flex flex-col gap-4">

            {/* Full Name */}
            <div className="group flex flex-col gap-2">
              <label className={labelBase}>Full Name</label>
              <div className={borderBase}>
                <input
                  type="text"
                  value={enquiryForm.name}
                  onChange={e => setEnquiryForm(f => ({ ...f, name: e.target.value }))}
                  className={inputBase}
                  autoComplete="name"
                />
              </div>
            </div>

            {/* Phone Number */}
            <div className="group flex flex-col gap-2">
              <label className={labelBase}>Phone Number</label>
              <div className={`flex items-end gap-3 ${borderBase}`}>
                <span className="font-dm-sans text-[15px] font-light text-[rgba(212,168,67,0.75)] shrink-0 leading-none">+91</span>
                <div className="w-px h-3.5 bg-white/20 self-end mb-0.5 shrink-0" />
                <input
                  type="tel"
                  value={enquiryForm.phone}
                  onChange={e => setEnquiryForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, '') }))}
                  className="flex-1 bg-transparent text-white font-dm-sans text-[15px] font-light outline-none caret-[#D4A843]"
                  autoComplete="tel"
                />
              </div>
            </div>

            {/* Preferred Date */}
            <div className="group flex flex-col gap-2">
              <label className={labelBase}>Preferred Date</label>
              <div className={borderBase}>
                <input
                  type="date"
                  value={enquiryForm.date}
                  min={(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })()}
                  onChange={e => setEnquiryForm(f => ({ ...f, date: e.target.value }))}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  className={`${inputBase} [color-scheme:dark]`}
                />
              </div>
            </div>

            {/* Cruise Preference */}
            <div className="group flex flex-col gap-2">
              <label className={labelBase}>Cruise Preference</label>
              <div className={`relative ${borderBase}`}>
                <select
                  value={enquiryForm.cruise}
                  onChange={e => {
                    const cruise = e.target.value;
                    setEnquiryForm(f => ({
                      ...f,
                      cruise,
                      deck: cruise === 'dinner' ? 'middle' : (f.cruise === 'dinner' ? '' : f.deck),
                    }));
                  }}
                  className={`${inputBase} appearance-none pr-5 cursor-pointer ${enquiryForm.cruise ? 'text-white' : 'text-white/55'}`}
                >
                  <option value="" disabled>Select cruise</option>
                  {ENQUIRY_CRUISES.map((c) => {
                    // Option text only: short display name, stored name until it loads.
                    const shown = cruiseLabel(cruiseNames[c.cruiseName], 'short') || c.cruiseName;
                    return (
                      <option key={c.value} value={c.value}>
                        {cruiseTimes[c.cruiseName] ? `${shown} (${cruiseTimes[c.cruiseName]})` : shown}
                      </option>
                    );
                  })}
                </select>
                <span className="absolute right-0 bottom-3 pointer-events-none text-[rgba(212,168,67,0.5)] text-[10px] leading-none">&#9660;</span>
              </div>
            </div>

            {/* Deck Preference */}
            <div className="flex flex-col gap-2">
              <label className={`font-dm-sans text-[13px] uppercase tracking-[0.14em] font-medium transition-colors duration-300 ${enquiryForm.cruise === 'dinner' ? 'text-white/20' : 'text-white/60 group-focus-within:text-[rgba(212,168,67,0.85)]'}`}>
                Deck Preference
              </label>
              <div className={`group relative border-b pb-2.5 transition-colors duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] ${enquiryForm.cruise === 'dinner' ? 'border-white/[0.07]' : 'border-white/[0.13] group-focus-within:border-[rgba(212,168,67,0.55)]'}`}>
                <select
                  value={enquiryForm.deck}
                  disabled={enquiryForm.cruise === 'dinner'}
                  onChange={e => setEnquiryForm(f => ({ ...f, deck: e.target.value }))}
                  className={`${inputBase} appearance-none pr-5 ${enquiryForm.cruise === 'dinner' ? 'text-white/30 cursor-not-allowed' : `cursor-pointer ${enquiryForm.deck ? 'text-white' : 'text-white/55'}`}`}
                >
                  <option value="" disabled>Select deck</option>
                  <option value="upper">Upper Deck</option>
                  {/* Value stays 'middle'; the Dinner Cruise shows it under its guest-facing name */}
                  <option value="middle">{enquiryForm.cruise === 'dinner' ? 'Performance Deck' : 'Middle Deck'}</option>
                  <option value="none">No Preference</option>
                </select>
                {enquiryForm.cruise !== 'dinner' && (
                  <span className="absolute right-0 bottom-3 pointer-events-none text-[rgba(212,168,67,0.5)] text-[10px] leading-none">&#9660;</span>
                )}
              </div>
              {enquiryForm.cruise === 'dinner' && (
                <p className="font-dm-sans text-sm text-[rgba(212,168,67,0.7)] font-light -mt-0.5">
                  Dinner Cruise is held on the Performance Deck
                </p>
              )}
            </div>

            {/* Number of Guests */}
            <div className="flex flex-col gap-2">
              <label className="font-dm-sans text-[13px] uppercase tracking-[0.14em] font-medium text-white/60">
                Number of Guests
              </label>
              <div className="flex flex-col">
                {[
                  { key: 'adults',   label: 'Adults',   sub: null,          min: 1 },
                  { key: 'children', label: 'Children', sub: '3–12 yrs',    min: 0 },
                  { key: 'infants',  label: 'Infants',  sub: 'under 3 yrs', min: 0 },
                ].map(({ key, label, sub, min }, i, arr) => (
                  <div
                    key={key}
                    className={`flex items-center justify-between py-2.5 ${i < arr.length - 1 ? 'border-b border-white/[0.07]' : ''}`}
                  >
                    <span className="font-dm-sans text-[15px] font-light text-white">
                      {label}
                      {sub && <span className="text-white/55 text-sm ml-1.5">{sub}</span>}
                    </span>
                    <div className="flex items-center gap-2.5">
                      <button
                        type="button"
                        onClick={() => setEnquiryForm(f => ({ ...f, [key]: Math.max(min, f[key] - 1) }))}
                        aria-label={`Decrease ${label}`}
                        className={counterBtn}
                      >
                        -
                      </button>
                      <span className="w-5 text-center font-dm-sans text-[15px] font-light text-white tabular-nums">
                        {enquiryForm[key]}
                      </span>
                      <button
                        type="button"
                        onClick={() => setEnquiryForm(f => ({ ...f, [key]: Math.min(50, f[key] + 1) }))}
                        aria-label={`Increase ${label}`}
                        className={counterBtn}
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Send Enquiry */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!enquiryValid || submitting}
            className={`w-full mt-6 py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,opacity] duration-[200ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none ${
              enquiryValid && !submitting
                ? 'opacity-100 active:scale-[0.97] hover:bg-[#B8973E] focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35] cursor-pointer'
                : 'opacity-30 cursor-not-allowed'
            }`}
          >
            {submitting ? 'Sending…' : 'Send Enquiry'}
          </button>

          {submitError && (
            <p className="font-dm-sans text-center text-sm text-[#E08A8A] mt-3 leading-relaxed">
              {submitError}
            </p>
          )}

          <p className="font-dm-sans text-center text-sm text-white/55 mt-3 leading-relaxed">
            We will contact you closer to the date
          </p>
          <p className="font-dm-sans text-center text-sm text-white/60 mt-1 leading-relaxed px-1">
            Note: Submitting an enquiry does not guarantee a table. We contact guests based on booking availability.
          </p>

        </div>
      </div>
  );
}
